import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {
  Text,
  Card,
  Chip,
  Appbar,
  ActivityIndicator,
  Snackbar,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {optionTypeApi} from '../../services/api/optionTypeApi';
import {colors, spacing} from '../../theme';

const RENDER_TYPE_LABELS = {
  TOGGLE: 'toggle',
  SINGLE_SELECT: 'singleSelect',
  MULTI_SELECT: 'multiSelect',
  TEXT: 'text',
};

const OptionTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [optionTypes, setOptionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Edit dialog state
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, null, true);
      setOptionTypes(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (item, newValue) => {
    try {
      await optionTypeApi.toggleOptionType(item.id, {isEnabled: newValue});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchData, t]);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setDisplayName(item.name);
    setSortOrder(String(item.displayOrder));
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingItem) return;
    try {
      await optionTypeApi.updateOverride(editingItem.id, {
        displayNameOverride: displayName.trim() || null,
        sortOrderOverride: sortOrder ? parseInt(sortOrder, 10) : null,
      });
      setDialogVisible(false);
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingItem, displayName, sortOrder, fetchData, t]);

  if (isLoading && optionTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('optionType.title')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {optionTypes.length === 0 ? (
          <Text style={styles.emptyText}>{t('optionType.noData')}</Text>
        ) : (
          optionTypes.slice().sort((a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0)).map(optionType => (
            <TouchableOpacity
              key={optionType.id}
              onPress={() => navigation.navigate('OptionTypeDetail', {optionType})}
              activeOpacity={0.7}>
              <Card style={[styles.card, !optionType.enabled && styles.cardDisabled]}>
                <Card.Content style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <IconButton
                      icon={optionType.enabled ? 'check-circle' : 'circle-outline'}
                      size={24}
                      iconColor={optionType.enabled ? colors.primary : colors.textSecondary}
                      onPress={() => handleToggle(optionType, !optionType.enabled)}
                      style={styles.toggleBtn}
                    />
                    <View style={styles.cardText}>
                      <Text variant="titleMedium" style={[styles.featureName, !optionType.enabled && styles.textDisabled]}>
                        {optionType.name}
                      </Text>
                    </View>
                    <IconButton
                      icon="format-list-bulleted"
                      size={20}
                      iconColor={colors.textSecondary}
                      onPress={() => navigation.navigate('OptionTypeDetail', {optionType})}
                      style={styles.actionBtn}
                    />
                    <IconButton
                      icon="pencil-outline"
                      size={20}
                      iconColor={colors.textSecondary}
                      onPress={() => openEditDialog(optionType)}
                      style={styles.actionBtn}
                    />
                  </View>
                </Card.Content>
              </Card>
            </TouchableOpacity>
          ))
        )}
        <View style={{height: 40}} />
      </ScrollView>

      {/* Edit Override Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{t('optionType.editOverride')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('optionType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveOverride}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  card: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  cardContent: {paddingVertical: spacing.sm},
  cardHeader: {flexDirection: 'row', alignItems: 'center'},
  toggleBtn: {margin: 0},
  cardText: {flex: 1},
  featureName: {fontWeight: '600', color: colors.text},
  textDisabled: {color: colors.textSecondary},
  badges: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs},
  renderChip: {height: 24, paddingVertical: 0, backgroundColor: colors.secondary + '15'},
  genderChip: {height: 24, paddingVertical: 0, backgroundColor: colors.primary + '15'},
  actionBtn: {margin: 0},
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default OptionTypeListScreen;
